import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useAccessToken } from "src/api/useAccessToken";
import { useSnackBar } from "src/components/Reusable/CustomSnackbarProvider";
import { useRealmApp } from "src/store/RealmApp";

export const intervals = {
  intervals: () => ["intervals"] as const,
};

export type IntervalStart =
  | {
      type: "fixed";
      fixedPoint: "projectStartDate" | "projectCreatedAt";
    }
  | {
      type: "keyMoment";
      keyMoment: {
        _id: string;
        name: string;
      };
      occurrenceRule: "first" | "last";
    };

export type IntervalEnd = IntervalStart;

export type IntervalData = {
  _id: string;
  name: string;
  description: string;
  start: IntervalStart;
  end: IntervalEnd;
  createdBy: {
    _id: string;
    name: string;
    email: string;
  };
  instanceId: string;
  createdAt: string;
  updatedAt: string;
};

export const useGetIntervals = () => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    intervals.intervals(),
    async (): Promise<IntervalData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/keyIntervals`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useCreateInterval = () => {
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (interval: {
      name: string;
      description?: string;
      start: {
        type: "fixed";
        fixedPoint: "projectStartDate" | "projectCreatedAt";
      } | {
        type: "keyMoment";
        keyMomentId: string;
        occurrenceRule: "first" | "last";
      };
      end: {
        type: "fixed";
        fixedPoint: "projectStartDate" | "projectCreatedAt";
      } | {
        type: "keyMoment";
        keyMomentId: string;
        occurrenceRule: "first" | "last";
      };
    }): Promise<{ message: string; id: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/keyInterval`,
        {
          ...interval,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error creating interval", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(intervals.intervals());
    },
  });
};

export const useUpdateInterval = () => {
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();
  return useMutation({
    mutationFn: async (interval: {
      _id: string;
      name?: string;
      description?: string;
      start?: IntervalStart;
      end?: IntervalEnd;
    }): Promise<{ message: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.post(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/updateKeyInterval`,
        {
          ...interval,
        },
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error updating interval", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(intervals.intervals());
    },
  });
};

export const useDeleteInterval = () => {
  const snackbarCtx = useSnackBar();
  const queryClient = useQueryClient();
  const getValidAccessToken = useAccessToken();

  return useMutation({
    mutationFn: async (_id: string): Promise<{ message: string }> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.delete(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/keyInterval`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          params: {
            _id,
          },
        }
      );
      return res.data;
    },
    onError: () => {
      snackbarCtx.showSnackbar("Error deleting interval", "error");
    },
    onSuccess: () => {
      queryClient.invalidateQueries(intervals.intervals());
    },
  });
};
